import { useMemo } from 'react'

import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { ColumnDef } from '@tanstack/react-table'
import { EyeIcon } from 'lucide-react'

import { JobUsersPageQuery } from '@/_gql/graphql'

import {
  DataTable,
  useDataTable,
  useDataTableState,
} from '@/components/extended-ui/data-table'
import { Button } from '@/components/ui/button'

import { useDebounce } from '@/lib/hooks/use-debounce'

import { fuzzyFilterFn } from '@/lib/utils/datatable'

import { queryJobUsersPage } from '../query'

interface TableJobUsersProps {
  jobId?: number
}

export function TableJobUsers({ jobId }: TableJobUsersProps) {
  const { pagination, setPagination, search, setSearch } = useDataTableState()
  const page = useDebounce(pagination, 400)
  const query = useQuery({
    ...queryJobUsersPage({ page, search: search, jobId }),
    placeholderData: keepPreviousData,
  })

  const columns = useMemo(getColumns, [])

  const table = useDataTable({
    columns,
    data: query.data?.data || [],
    isLoading: query.isLoading,
    rowCount: query.data?.page.total,
    filterFns: { fuzzy: fuzzyFilterFn },
    globalFilterFn: 'fuzzy',
    pagination: { onChange: setPagination, state: pagination },
    search: { onChange: setSearch, state: search },
  })

  return (
    <DataTable.Root table={table}>
      <DataTable.Toolbar
        filters={[
          {
            id: 'search',
            type: 'text',
            placeholder: 'Cari Pelamar...',
            value: table.getSearch(),
            onChange: table.onSearchChange,
          },
        ]}
      />
      <DataTable.Table />
    </DataTable.Root>
  )
}

type JobUser = NonNullable<JobUsersPageQuery['jobUsersPage']['data'][number]>

function getColumns(): ColumnDef<JobUser>[] {
  return [
    {
      accessorKey: 'id',
      header: 'ID',
    },
    {
      accessorKey: 'name',
      header: 'Pelamar',
    },
    {
      accessorKey: 'title',
      header: 'Job Listing',
    },
    {
      accessorKey: 'companyName',
      header: 'Perusahaan',
    },
    {
      id: 'actions',
      cell: ({ cell }) => {
        const { id, title } = cell.row.original
        return (
          <div className="flex gap-1.5">
            <Link
              to="/admin-v2/job/applicant/$applicantId"
              params={{ applicantId: String(id) }}
            >
              <Button
                size="icon"
                className="size-8 text-primary-yes"
                variant="ghost"
              >
                <EyeIcon />
              </Button>
            </Link>
          </div>
        )
      },
    },
  ]
}
